import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import {
  StandardEvents,
  ProductSelectEvent,
  CartLinesUpdateEvent,
  CartErrorEvent,
} from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id]
 * @property {string} [title]
 * @property {string} [name]
 * @property {boolean} [available]
 * @property {Object} [featured_media]
 * @property {Object} [featured_media.preview_image]
 * @property {string} [featured_media.preview_image.src]
 * @property {string} [featured_media.alt]
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
  ];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {HTMLElement | null} */
  #footerElement = null;

  /** @type {HTMLElement | null} */
  #finalCtaElement = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {number | null} */
  #scrollRaf = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    /**
     * Lấy Product Form + Main Buy Button.
     */
    this.#setupElements();

    /**
     * Product variant events.
     */
    const target = this.closest('.shopify-section');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    /**
     * Cart events.
     */
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    /**
     * Quantity events.
     */
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    /**
     * ==========================================================
     * VISIBILITY TRACKING
     * ==========================================================
     *
     * Không dùng Footer IntersectionObserver nữa.
     *
     * Mỗi lần scroll:
     * - check Main Add to Cart
     * - check Final CTA (boundary thật sự của Sticky)
     * - check Footer (safety net, luôn active)
     * - quyết định show / hide Sticky
     */
    window.addEventListener(
      'scroll',
      this.#handleViewportChange,
      {
        passive: true,
        signal,
      }
    );

    window.addEventListener(
      'resize',
      this.#handleViewportChange,
      {
        passive: true,
        signal,
      }
    );

    window.addEventListener(
      'load',
      this.#handleViewportChange,
      {
        signal,
      }
    );

    this.#getInitialQuantity();

    /**
     * Kiểm tra trạng thái lần đầu.
     */
    requestAnimationFrame(() => {
      if (signal.aborted) return;

      this.#syncStickyVisibility();
    });

    /**
     * Shopify Chat.
     */
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;

      if (this.#isStuck && this.#isChatActive()) {
        this.#hideStickyBar();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    if (this.#scrollRaf) {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
  }

  /**
   * ============================================================
   * SETUP
   * ============================================================
   */
  #setupElements() {
    const productForm = this.#getProductForm();

    if (!productForm) return;

    this.#buyButtonsBlock =
      productForm.closest('.buy-buttons-block');

    this.#targetAddToCartButton =
      productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * ============================================================
   * SCROLL / RESIZE
   * ============================================================
   */
  #handleViewportChange = () => {
    /**
     * requestAnimationFrame giúp scroll mượt,
     * tránh chạy logic hàng trăm lần / giây.
     */
    if (this.#scrollRaf) return;

    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;

      this.#syncStickyVisibility();
    });
  };

  /**
   * ============================================================
   * FIND FINAL CTA (boundary thật sự của Sticky Add to Cart)
   * ============================================================
   *
   * `.ec-final-cta` là section "Final CTA" cuối cùng của trang trước khi
   * vào Footer (sections/emberdose-*-final-cta.liquid). Đây chính là
   * section user xác định trong DOM để làm END BOUNDARY — không hard-code
   * pixel, không dựa vào Footer detection (vốn không đáng tin cậy vì mỗi
   * theme render Footer một kiểu khác nhau).
   *
   * Nếu trang không có `.ec-final-cta` (vd. một sản phẩm khác dùng chung
   * component Sticky Add to Cart này trong tương lai), method trả về null
   * và `#syncStickyVisibility` sẽ chỉ dựa vào Footer-detection.
   */
  #getFinalCtaElement() {
    if (
      this.#finalCtaElement &&
      this.#finalCtaElement.isConnected
    ) {
      return this.#finalCtaElement;
    }

    this.#finalCtaElement = null;

    const element = document.querySelector('.ec-final-cta');

    if (element instanceof HTMLElement) {
      const rect = element.getBoundingClientRect();

      if (rect.width > 0 && rect.height > 0) {
        this.#finalCtaElement = element;

        return element;
      }
    }

    return null;
  }

  /**
   * ============================================================
   * FIND FOOTER (safety net — luôn được check, không chỉ fallback)
   * ============================================================
   *
   * Theme Shopify có thể dùng nhiều cấu trúc Footer khác nhau.
   *
   * Code sẽ thử nhiều selector.
   */
  #getFooterElement() {
    /**
     * Nếu đã tìm được Footer trước đó
     * và Footer vẫn còn trong DOM
     * => sử dụng lại.
     */
    if (
      this.#footerElement &&
      this.#footerElement.isConnected
    ) {
      return this.#footerElement;
    }

    /**
     * Reset.
     */
    this.#footerElement = null;

    /**
     * Các selector Footer phổ biến.
     *
     * Ưu tiên footer thật trước.
     */
    const selectors = [
      'footer',

      /**
       * Shopify footer section IDs.
       */
      '[id^="shopify-section-"][id*="__footer"]',

      '#shopify-section-footer',

      /**
       * Shopify section group.
       */
      '.shopify-section-group-footer-group',

      /**
       * Theme footer class.
       */
      'footer.footer',

      '.footer',
    ];

    for (const selector of selectors) {
      const elements =
        document.querySelectorAll(selector);

      for (const element of elements) {
        if (!(element instanceof HTMLElement)) {
          continue;
        }

        const rect =
          element.getBoundingClientRect();

        /**
         * Bỏ qua những footer hidden / dummy.
         */
        if (
          rect.width > 0 &&
          rect.height > 0
        ) {
          this.#footerElement = element;

          return element;
        }
      }
    }

    return null;
  }

  /**
   * ============================================================
   * CHECK FOOTER VISIBILITY (safety net)
   * ============================================================
   */
  #isFooterVisible() {
    const footer =
      this.#getFooterElement();

    if (!footer) return false;

    const rect =
      footer.getBoundingClientRect();

    /**
     * Footer được coi là visible nếu:
     *
     * top Footer < bottom viewport
     *
     * và
     *
     * bottom Footer > top viewport
     */
    return (
      rect.top < window.innerHeight &&
      rect.bottom > 0
    );
  }

  /**
   * ============================================================
   * CHECK ĐÃ SCROLL QUA FINAL CTA CHƯA
   * ============================================================
   *
   * Final CTA là END BOUNDARY tuyệt đối:
   *
   * - Final CTA vẫn còn trong viewport (kể cả một phần) => CHƯA qua.
   * - Bottom của Final CTA đã đi lên trên top viewport (rect.bottom <= 0)
   *   => ĐÃ qua.
   *
   * Tính lại mỗi lần scroll nên tự động đúng cho cả 2 chiều:
   * - Scroll DOWN: qua khỏi Final CTA => hide.
   * - Scroll UP: quay lại Final CTA (hoặc phía trên nó) => show lại
   *   (nếu Main Add to Cart cũng đã qua khỏi viewport).
   */
  #isPastFinalCta() {
    const finalCta = this.#getFinalCtaElement();

    if (!finalCta) return null;

    const rect = finalCta.getBoundingClientRect();

    return rect.bottom <= 0;
  }

  /**
   * ============================================================
   * MASTER VISIBILITY LOGIC
   * ============================================================
   *
   * TẤT CẢ logic show/hide Sticky nằm tại đây.
   */
  #syncStickyVisibility() {
    /**
     * Nếu chưa cache được Main Buy Button
     * thì thử tìm lại.
     */
    if (
      !this.#buyButtonsBlock ||
      !this.#buyButtonsBlock.isConnected
    ) {
      this.#setupElements();
    }

    const buyButtonsBlock =
      this.#buyButtonsBlock;

    if (!buyButtonsBlock) {
      this.#hideStickyBar();

      return;
    }

    /**
     * ==========================================================
     * RULE #1
     *
     * FINAL CTA LÀ BOUNDARY, FOOTER LÀ SAFETY NET — CẢ HAI ĐỀU
     * LUÔN ĐƯỢC CHECK (OR), KHÔNG PHẢI CÁI NÀY LÀ FALLBACK CỦA CÁI KIA.
     *
     * Lý do bắt buộc phải OR cả hai, không chỉ dùng Final CTA:
     *
     * Trên trang thực tế, Footer nằm NGAY SAU Final CTA trong DOM
     * (không có khoảng trống). Khi user scroll xuống, Footer bắt đầu
     * lọt vào viewport (rect.top < innerHeight) SỚM HƠN nhiều so với
     * lúc Final CTA scroll hẳn ra khỏi viewport (rect.bottom <= 0) —
     * vì "Footer bắt đầu hiện" chỉ cần viewport chạm mép trên Footer,
     * còn "Final CTA đã qua hẳn" cần scroll thêm gần một viewport-height
     * nữa. Nếu chỉ dựa vào Final CTA, Sticky sẽ vẫn hiện đè lên Footer
     * trong khoảng thời gian đó — đúng bug đã report (đặc biệt rõ ở
     * cuối trang, khi Final CTA + Footer cùng lọt gọn trong viewport).
     *
     * Nên: ẨN NGAY khi (đã qua Final CTA) HOẶC (Footer đã bắt đầu hiện).
     * ==========================================================
     */
    const pastFinalCta =
      this.#isPastFinalCta();

    const footerVisible =
      this.#isFooterVisible();

    if (pastFinalCta === true || footerVisible) {
      this.#hiddenByBottom = true;

      this.#hideStickyBar();

      return;
    }

    /**
     * Chưa qua Final CTA và Footer chưa xuất hiện.
     */
    this.#hiddenByBottom = false;

    /**
     * ==========================================================
     * RULE #2
     *
     * CHECK MAIN ADD TO CART
     * ==========================================================
     */
    const buyButtonsRect =
      buyButtonsBlock.getBoundingClientRect();

    /**
     * Main Add to Cart đã scroll hoàn toàn
     * lên phía trên viewport.
     *
     * => Sticky được phép hiện.
     */
    const mainBuyButtonsPassed =
      buyButtonsRect.bottom < 0;

    if (mainBuyButtonsPassed) {
      /**
       * Shopify Chat đang active
       * => không hiện Sticky.
       */
      if (this.#isChatActive()) {
        this.#hideStickyBar();

        return;
      }

      this.#showStickyBar();

      return;
    }

    /**
     * Main Add to Cart chưa scroll qua
     * hoặc đang visible.
     *
     * => Sticky ẩn.
     */
    this.#hideStickyBar();
  }

  /**
   * ============================================================
   * ADD TO CART
   * ============================================================
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) {
      /**
       * Thử cache lại button nếu cần.
       */
      this.#setupElements();
    }

    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet =
      'true';

    this.#targetAddToCartButton.click();

    const cartIcon =
      document.querySelector(
        '.header-actions__cart-icon'
      );

    if (
      this.refs.addToCartButton.dataset.added !==
      'true'
    ) {
      this.refs.addToCartButton.dataset.added =
        'true';
    }

    if (
      !cartIcon ||
      !this.refs.addToCartButton ||
      !this.refs.productImage
    ) {
      return;
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    const flyToCartElement =
      /** @type {FlyToCart} */ (
        document.createElement('fly-to-cart')
      );

    flyToCartElement.classList.add(
      'fly-to-cart--sticky'
    );

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );

    flyToCartElement.useSourceSize = 'true';

    flyToCartElement.source =
      this.refs.productImage;

    flyToCartElement.destination =
      cartIcon;

    document.body.appendChild(
      flyToCartElement
    );

    await onAnimationEnd([
      this.refs.addToCartButton,
      flyToCartElement,
    ]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute(
        'data-added'
      );
    }, 800);
  };

  /**
   * ============================================================
   * PRODUCT VARIANT
   * ============================================================
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    const { optionValueId } =
      event.detail ?? {};

    if (optionValueId) {
      this.dataset.currentVariantId =
        optionValueId;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const {
          html,
          productId,
          resource: variant,
        } = detail;

        if (
          productId &&
          productId !== this.dataset.productId
        ) {
          return;
        }

        const newStickyAddToCart =
          /** @type {HTMLElement | null} */ (
            html.querySelector(
              'sticky-add-to-cart'
            )
          );

        if (!newStickyAddToCart) return;

        const newStickyBar =
          newStickyAddToCart.querySelector(
            '[ref="stickyBar"]'
          );

        if (!newStickyBar) return;

        /**
         * Save sticky visibility state.
         */
        const currentStuck =
          this.refs.stickyBar.getAttribute(
            'data-stuck'
          ) || 'false';

        const variantAvailable =
          newStickyAddToCart.dataset
            .variantAvailable;

        /**
         * Update Sticky HTML.
         */
        morph(
          this.refs.stickyBar,
          newStickyBar,
          {
            childrenOnly: true,
          }
        );

        /**
         * Restore visibility.
         */
        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );

        this.dataset.variantAvailable =
          variantAvailable;

        /**
         * Update variant ID.
         */
        if (variant && variant.id) {
          this.dataset.currentVariantId =
            variant.id;
        }

        /**
         * Re-cache product elements.
         */
        const productForm =
          this.#getProductForm();

        if (productForm) {
          this.#targetAddToCartButton =
            productForm.querySelector(
              '[ref="addToCartButton"]'
            );

          this.#buyButtonsBlock =
            productForm.closest(
              '.buy-buttons-block'
            );
        }

        /**
         * Handle unavailable variant.
         */
        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        this.#updateButtonText();

        /**
         * Quan trọng:
         *
         * Variant thay đổi xong thì check lại
         * Final CTA / Footer / Sticky state.
         */
        this.#handleViewportChange();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * ============================================================
   * VARIANT UNAVAILABLE
   * ============================================================
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement =
      this.querySelector(
        '.sticky-add-to-cart__variant'
      );

    const productId =
      this.dataset.productId;

    const variantPicker =
      document.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );

    if (
      !variantTitleElement ||
      !variantPicker
    ) {
      return;
    }

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll(
        'input:checked'
      )
    )
      .map(
        (option) =>
          /** @type {HTMLInputElement} */ (
            option
          ).value
      )
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent =
      selectedOptions;
  };

  /**
   * ============================================================
   * CART COMPLETE
   * ============================================================
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet =
          'false';
      }
    };

    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * ============================================================
   * QUANTITY
   * ============================================================
   */
  #handleQuantityUpdate = (event) => {
    /**
     * Ignore Cart Drawer.
     */
    if (event.detail.cartLine) return;

    this.#currentQuantity =
      event.detail.quantity;

    this.#updateButtonText();
  };

  /**
   * ============================================================
   * SHOW STICKY
   * ============================================================
   */
  #showStickyBar() {
    /**
     * Double safety:
     *
     * Nếu đã qua Final CTA hoặc Footer đang visible
     * thì tuyệt đối không show.
     */
    if (this.#hiddenByBottom) {
      this.#hideStickyBar();

      return;
    }

    /**
     * Shopify Chat.
     */
    if (this.#isChatActive()) return;

    /**
     * Đã show rồi.
     */
    if (this.#isStuck) return;

    const { stickyBar } = this.refs;

    this.#isStuck = true;

    stickyBar.dataset.stuck = 'true';
  }

  /**
   * ============================================================
   * HIDE STICKY
   * ============================================================
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = false;

    stickyBar.dataset.stuck = 'false';
  }

  /**
   * ============================================================
   * SHOPIFY CHAT
   * ============================================================
   */
  #isChatActive() {
    if (
      !customElements.get('shopify-chat')
    ) {
      return false;
    }

    return Boolean(
      document.querySelector('shopify-chat')
    );
  }

  /**
   * ============================================================
   * GET PRODUCT FORM
   * ============================================================
   */
  #getProductForm() {
    const productId =
      this.dataset.productId;

    if (!productId) return null;

    const sectionElement =
      this.closest('.shopify-section');

    if (!sectionElement) return null;

    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * ============================================================
   * INITIAL QUANTITY
   * ============================================================
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(
        this.dataset.initialQuantity || '1'
      ) || 1;

    this.#updateButtonText();
  }

  /**
   * ============================================================
   * UPDATE QUANTITY DISPLAY
   * ============================================================
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;

    const available =
      !addToCartButton.disabled;

    quantityNumber.textContent =
      this.#currentQuantity.toString();

    if (
      available &&
      this.#currentQuantity > 1
    ) {
      quantityDisplay.style.display =
        'inline';
    } else {
      quantityDisplay.style.display =
        'none';
    }
  }
}

/**
 * Register custom element.
 */
if (
  !customElements.get(
    'sticky-add-to-cart'
  )
) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}
